import { log } from '@/logger/logger.ts';
import { HandleType } from '@/enums/handle-type.enum';
import { ModelType } from '@/enums/model-type.enum';
import { ModelAssetToUploadedAsset, UploadedAssetToModelAsset, type UploadedAsset } from '@/types/api/assets';
import { NodeViewMode } from '@/enums/node-view-mode.enum';
import { isMediaAsset } from '@/components/Common/ImageList/utils';
import { getModelIdentifier, widenedInputDataTypes } from '@/utils/nodeInputValidation';
import {
  describeResults,
  isResultDiagnosticsEnabled,
  logResultDiagnostic,
  type ResultDeliveryContext,
} from '../RunFlow/resultDiagnostics';
import { UndoRedoEntry } from '../undoRedo';
import { handleTypeToDataType, type FlowGraph } from '../FlowGraph';
import { selectedIndexAfterRemovingFile } from './BaseFileView';
import { ImportModelView } from './ImportModelView';
import type { DownloadableAsset } from '@/types/downloadable';
import type { NodeId } from 'web';
import type { AiModelData, ModelBaseNodeData } from '@/types/nodes/model';
import type { ClearableAssetsView } from './NodeAssetManager';

const logger = log.getLogger('ModelBaseView');

const FLUX_3_VIDEO_MODEL = 'blackforestlabs/flux-3/text-to-video';

const FLUX_3_END_IMAGE_KEY = 'end_image_url';

export interface DynamicInputConfig {
  prefix: string;
  handleType: HandleType;
  /**
   * Connection types this input accepts beyond `handleType`, for slots that take mixed media.
   * The handle still renders as `handleType`; the accepted set lives in the node's valid input
   * types (see {@link widenedInputDataTypes}).
   */
  acceptTypes?: HandleType[];
  /** Optional human-readable label prefix. Defaults to `prefix` (the handle key). */
  labelPrefix?: string;
  /**
   * Key of the handle new inputs are inserted before, which is pushed down to make room.
   * Inputs are appended after every existing handle when unset.
   */
  insertBefore?: string;
}

export const mergeSeedParam = (params: ModelBaseNodeData['params'], result?: ModelBaseNodeData['result'][number]) => {
  if (!result || !params || !params.seed) {
    return params;
  }
  if (!('input' in result && 'seed' in result.input)) {
    return params;
  }
  const resultSeed = result.input.seed;
  if (typeof resultSeed !== 'number') {
    return params;
  }
  const seedParam = params.seed as { isRandom: boolean; seed: number };
  return {
    ...params,
    seed: { ...seedParam, seed: resultSeed },
  };
};

export class ModelBaseView implements ClearableAssetsView {
  constructor(
    private graph: FlowGraph,
    private nodeId: NodeId,
  ) {}

  setSelectedIndex(newSelectedOutput: number) {
    this.graph.edit(() => this.updateIndex(newSelectedOutput));
    this.graph.updateNodeOutputs(this.nodeId, false);
    this.graph.loadImageResources();
  }

  appendResult(
    resultsToAppend: UploadedAsset[],
    runIndexInBatch: number,
    batchId: string,
    secondaryOrder: number,
    delivery?: ResultDeliveryContext,
  ) {
    if (resultsToAppend.length === 0) {
      return;
    }
    const currentResult = this.getGenerations();
    const diagnostic = () => ({
      ...delivery,
      nodeId: this.nodeId,
      batchId,
      runIndexInBatch,
      secondaryOrder,
      ...describeResults(resultsToAppend),
      previousResultCount: currentResult.length,
    });
    if (hasExistingGenerationForIncomingRunId(currentResult, runIndexInBatch, batchId, secondaryOrder)) {
      logResultDiagnostic('workflow_result_append_decision', () => ({
        ...diagnostic(),
        outcome: 'existing_generation',
      }));
      logger.warn('appendResult skipped: generation already exists for this batch run slot', {
        nodeId: this.nodeId,
        batchId,
        runIndexInBatch,
        secondaryOrder,
      });
      return;
    }
    const index = findInsertionIndex(currentResult, runIndexInBatch, batchId, secondaryOrder);
    const newResultArray = [...currentResult.slice(0, index), ...resultsToAppend, ...currentResult.slice(index)];
    const newSelectedIndex = index + resultsToAppend.length - 1;

    this.updateGenerations(newResultArray);
    this.updateIndex(newSelectedIndex);
    logResultDiagnostic('workflow_result_append_decision', () => ({
      ...diagnostic(),
      outcome: 'appended',
      remainingResultCount: newResultArray.length,
    }));
    this.graph.updateNodeOutputs(this.nodeId, false);
    this.graph.loadResources();
  }

  /** Returns an undo entry for deleting one generation. Caller must wrap in `graph.edit()`. */
  deleteResultUndoEntry(index?: number): UndoRedoEntry | null {
    const generations = this.getGenerations();
    const indexToRemove = index ?? this.getSelectedIndex();
    if (indexToRemove < 0 || indexToRemove >= generations.length) {
      return null;
    }
    const currentSelectedIndex = this.getSelectedIndex();
    const newSelectedIndex = selectedIndexAfterRemovingFile(currentSelectedIndex, indexToRemove, generations.length);
    const newResult = generations.filter((_, i) => i !== indexToRemove);
    const undoEntry = this.updateGenerations(newResult);
    if (newResult.length > 0 && newSelectedIndex !== currentSelectedIndex) {
      undoEntry.add(this.updateIndex(newSelectedIndex));
    }
    return undoEntry;
  }

  deleteResult(index?: number) {
    const undoEntry = this.deleteResultUndoEntry(index);
    if (!undoEntry) {
      return;
    }
    this.graph.edit(() => undoEntry);
    this.graph.updateNodeOutputs(this.nodeId, false);
    this.graph.loadResources();
  }

  deleteAllOtherResults(index?: number) {
    const generations = this.getGenerations();
    const selectedOutput = index ?? this.getSelectedIndex();
    const newResult = generations.filter((_, index) => index === selectedOutput);
    this.graph.edit(() => this.updateGenerations(newResult));
    this.graph.updateNodeOutputs(this.nodeId, false);
    this.graph.loadResources();
  }

  deleteAllResults() {
    this.graph.edit(() => this.updateGenerations([]));
    this.graph.updateNodeOutputs(this.nodeId, false);
    this.graph.loadResources();
  }

  /**
   * Clears all assets from this view (returns UndoRedoEntry for batching with other operations)
   */
  clearAllAssets(): UndoRedoEntry {
    return this.updateGenerations([]);
  }

  /**
   * Checks if this view has any assets
   */
  hasAssets(): boolean {
    return this.getGenerations().length > 0;
  }

  /**
   * Clears the currently selected generation (returns UndoRedoEntry for batching)
   */
  clearCurrentAsset(): UndoRedoEntry {
    return this.deleteResultUndoEntry() ?? UndoRedoEntry.empty();
  }

  /**
   * Clears all assets except the currently selected one (returns UndoRedoEntry for batching)
   * Uses skipPostProcessing so the batch caller (e.g. NodeAssetManager.clearOtherAssetsFromNodes) can run loadResources/updateNodeOutputs once.
   */
  clearOtherAssets(): UndoRedoEntry {
    const generations = this.getGenerations();
    const selectedOutput = this.getSelectedIndex();
    const newResult = generations.filter((_, index) => index === selectedOutput);
    return this.updateGenerations(newResult);
  }

  hasMultipleAssets(): boolean {
    return this.getGenerations().length > 1;
  }

  editText(index: number, newValue: string) {
    this.graph.wasmChange((wasm) => wasm.editTextAsset(this.nodeId, newValue));
    this.graph.updateNodeOutputs(this.nodeId, false);
  }

  private updateIndex(newSelectedOutput: number): UndoRedoEntry {
    const undoEntry = UndoRedoEntry.empty();
    const { params } = this.getData();
    undoEntry.add(this.graph.wasmChange((wasm) => wasm.setSelectedMedia(this.nodeId, newSelectedOutput)));
    const newParams = mergeSeedParam(params, this.getGenerations()[newSelectedOutput]);
    if (newParams !== params) {
      undoEntry.add(
        this.graph.updateModelNodeData(this.nodeId, {
          params: newParams,
        }),
      );
    }
    return undoEntry;
  }

  private updateGenerations(newGenerations: UploadedAsset[]): UndoRedoEntry {
    const retainedIds = isResultDiagnosticsEnabled()
      ? new Set(newGenerations.flatMap((asset) => ('id' in asset ? [asset.id] : [])))
      : undefined;
    const removed = retainedIds
      ? this.getGenerations().filter((asset) => 'id' in asset && !retainedIds.has(asset.id))
      : [];
    const currentIndex = this.getSelectedIndex();
    const undoEntry = UndoRedoEntry.empty();
    const { params: currentParams } = this.getData();
    undoEntry.add(
      this.graph.wasmChange((wasm) =>
        wasm.setModelGenerations(
          this.nodeId,
          newGenerations.map((asset) => {
            const modelAsset = UploadedAssetToModelAsset(asset);
            if (modelAsset === undefined) {
              throw new Error(`Invalid media asset: ${asset.type}`);
            }
            return modelAsset;
          }),
        ),
      ),
    );
    if (newGenerations.length - 1 < currentIndex) {
      undoEntry.add(this.graph.wasmChange((wasm) => wasm.setSelectedMedia(this.nodeId, 0)));
      const newParams = mergeSeedParam(currentParams, newGenerations[0]);
      if (newParams !== currentParams) {
        undoEntry.add(this.graph.updateModelNodeData(this.nodeId, { params: newParams }));
      }
    }
    if (removed.length) {
      logResultDiagnostic('workflow_result_deleted', () => ({
        nodeId: this.nodeId,
        ...describeResults(removed),
        remainingResultCount: newGenerations.length,
      }));
    }
    return undoEntry;
  }

  private addInputHandleInternal({
    type,
    key,
    order,
    format,
    label,
    description = '',
  }: {
    type: HandleType;
    key: string;
    order: number;
    format: string;
    label?: string;
    description?: string;
  }): UndoRedoEntry {
    const undoEntry = UndoRedoEntry.empty();
    const validType = handleTypeToDataType(type);
    // The widened list has to match what `validateInputs` recomputes on load, or the handle would
    // narrow back to `type` on reload and drop any edge only the wider set allowed.
    const data = this.graph.store.getNodeDataNullable<AiModelData>(this.nodeId);
    const validInputTypes =
      (data ? widenedInputDataTypes(getModelIdentifier(data), key) : undefined) ??
      (validType ? [validType] : undefined);
    undoEntry.add(this.graph.wasmChange((wasm) => wasm.addInputHandle(this.nodeId, key, validInputTypes)));

    undoEntry.add(
      this.graph.addNodeInputHandle(this.nodeId, key, {
        description,
        format,
        id: crypto.randomUUID(),
        label: label ?? key,
        order,
        required: false,
        type,
      }),
    );
    return undoEntry;
  }

  /**
   * Determines dynamic input configs (prefix + handle type) for a model.
   * Returns an empty array for models that don't support standard dynamic inputs
   * (e.g. FalFluxMultiLora uses lora handles instead).
   */
  getDynamicInputConfigs(modelName: string): DynamicInputConfig[] {
    const isFalFluxMultiLora =
      modelName === ModelType.FalFluxMultiLora.valueOf() ||
      modelName === ModelType.FalFluxMultiLoraInpaint.valueOf() ||
      modelName === ModelType.FalFluxKontextLora.valueOf();
    if (isFalFluxMultiLora) return [];

    if (modelName === 'fal-ai/kling-video/o3/pro/video-to-video/edit') {
      return [
        { prefix: 'image', handleType: HandleType.Image },
        { prefix: 'element', handleType: HandleType.KlingElement },
      ];
    }

    const klingType = ModelType.Kling.valueOf();
    const data = this.graph.store.getNodeDataNullable<AiModelData>(this.nodeId);
    const isKling3Model =
      (modelName === klingType && data?.kind?.type === klingType) ||
      modelName === 'fal-ai/kling-video/v2.6/standard/motion-control' ||
      modelName === 'fal-ai/kling-video/v2.6/pro/motion-control';
    if (isKling3Model) return [{ prefix: 'element', handleType: HandleType.KlingElement }];

    if (modelName === ModelType.RunwayAleph2.valueOf()) {
      return [{ prefix: 'keyframe', handleType: HandleType.Keyframe, labelPrefix: 'keyframe' }];
    }

    // A Flux 3 keyframe sits at a position inside the video, so it belongs between the Image and
    // End Image handles rather than after them.
    if (modelName === FLUX_3_VIDEO_MODEL) {
      return [
        {
          prefix: 'keyframe',
          handleType: HandleType.Keyframe,
          labelPrefix: 'keyframe',
          insertBefore: FLUX_3_END_IMAGE_KEY,
        },
      ];
    }

    if (
      modelName === 'bytedance/seedance-2.0/reference-to-video' ||
      modelName === 'bytedance/seedance-2.0/mini/reference-to-video' ||
      modelName === 'bytedance/seedance-2.5/reference-to-video'
    ) {
      return [{ prefix: 'reference', handleType: HandleType.Any }];
    }

    if (modelName === 'google/gemini-omni-flash-preview/reference-to-video') {
      return [{ prefix: 'reference', handleType: HandleType.Any }];
    }

    if (modelName === 'minimax/h3/reference-to-video') {
      return [{ prefix: 'reference', handleType: HandleType.Any }];
    }

    if (modelName === 'alibaba/wan-3.0/reference-to-video') {
      return [{ prefix: 'reference', handleType: HandleType.Any }];
    }

    if (modelName === 'kling/v4/reference-to-video') {
      return [
        { prefix: 'reference', handleType: HandleType.Any, labelPrefix: 'Reference' },
        { prefix: 'element', handleType: HandleType.KlingElement },
      ];
    }

    if (modelName === 'kling/v4/video-to-video/edit') {
      return [
        { prefix: 'reference', handleType: HandleType.Image, labelPrefix: 'Image' },
        { prefix: 'element', handleType: HandleType.KlingElement },
      ];
    }

    // Ideogram V4.5 Edit takes only images as references. `reference`, not `image`: the runner folds
    // every `image*` key into one array, which would swallow the primary `image_url`.
    if (modelName === 'ideogram/v4.5/edit') {
      return [{ prefix: 'reference', handleType: HandleType.Image, labelPrefix: 'Reference Image' }];
    }

    // Seed Audio ships as two nodes on the same modelId. The image node carries an image_url
    // handle; every other Seed Audio node is the audio node and gets dynamic audio inputs.
    if (modelName === 'bytedance/seed-audio-1.0') {
      const nodeData = this.graph.store.getNodeDataNullable<ModelBaseNodeData>(this.nodeId);
      const inputHandles = nodeData?.handles?.input;
      const isImageNode = !!inputHandles && !Array.isArray(inputHandles) && 'image_url' in inputHandles;
      return isImageNode ? [] : [{ prefix: 'audio', handleType: HandleType.Audio, labelPrefix: 'Audio' }];
    }

    if (modelName === 'google/gemini-omni-flash/reference-to-video') {
      return [{ prefix: 'image', handleType: HandleType.Image }];
    }

    if (modelName === 'openrouter/router/video/enterprise') {
      return [{ prefix: 'video', handleType: HandleType.Video }];
    }

    if (modelName === ModelType.Reve21Remix.valueOf()) {
      return [{ prefix: 'reference_image', handleType: HandleType.Image, labelPrefix: 'Reference Image' }];
    }

    if (modelName === 'lyria-3') {
      return [{ prefix: 'image', handleType: HandleType.Image, labelPrefix: 'Image' }];
    }

    // World Labs takes an image, a set of images, or a video, so its media inputs accept either.
    // The `image` prefix is kept so the handle keys stay `image_N` for the request consolidation.
    if (modelName === ModelType.WorldLabs01.valueOf()) {
      return [
        {
          prefix: 'image',
          handleType: HandleType.Image,
          acceptTypes: [HandleType.Image, HandleType.Video],
          labelPrefix: 'Input',
        },
      ];
    }

    const importModelView = new ImportModelView(this.graph, this.nodeId);
    const dynamicHandleName = importModelView.getDynamicHandleName(modelName);
    if (!dynamicHandleName) return [];

    if (modelName === 'lucataco/video-merge') {
      return [{ prefix: dynamicHandleName, handleType: HandleType.Video }];
    }

    return [{ prefix: dynamicHandleName, handleType: HandleType.Image }];
  }

  addInputHandle(config: DynamicInputConfig) {
    this.graph.edit(() => {
      return this.addInputHandleWithKey(config).undoEntry;
    });
  }

  /**
   * Adds a dynamic input handle and returns the new handle key along with the undo entry.
   * Does NOT wrap in graph.edit() — the caller is responsible for that.
   */
  addInputHandleWithKey({ prefix, handleType, labelPrefix, insertBefore }: DynamicInputConfig): {
    key: string;
    undoEntry: UndoRedoEntry;
  } {
    const { handles, model } = this.graph.store.getNodeData<ModelBaseNodeData>(this.nodeId);
    // Derive the next index from the highest existing one, not the count: deleting a middle handle
    // (e.g. reference_2) leaves a gap, so count + 1 would collide with an existing key — and
    // addNodeInputHandle's object-spread merge would silently overwrite it, clobbering its edge.
    const indexPattern = new RegExp(`^${prefix}_(\\d+)$`);
    const initialMaxIndex = prefix in handles.input ? 1 : 0;
    const maxIndex = Object.keys(handles.input).reduce((max, key) => {
      const match = key.match(indexPattern);
      return match ? Math.max(max, parseInt(match[1], 10)) : max;
    }, initialMaxIndex);
    const nextIndex = maxIndex + 1;
    const newKey = `${prefix}_${nextIndex}`;
    const label = labelPrefix ? `${labelPrefix} ${nextIndex}` : undefined;

    const anchorOrder = insertBefore ? handles.input[insertBefore]?.order : undefined;
    // Append after the highest existing order, not at the count: orders can be sparse (the Image
    // Describer's lone `image` is order 2), and a count-based order would sort the new handle above
    // it, sliding the original down while React Flow measures it at its old row.
    const order = anchorOrder ?? Math.max(-1, ...Object.values(handles.input).map((handle) => handle.order || 0)) + 1;
    const shifted =
      anchorOrder === undefined
        ? []
        : Object.entries(handles.input).filter(([, handle]) => handle.order >= anchorOrder);

    const undoEntry = this.addInputHandleInternal({
      type: handleType,
      key: newKey,
      order,
      format: 'uri',
      label,
      description: model?.name?.startsWith('kling/v4/')
        ? Object.entries(handles.input).find(([key]) => key.startsWith(`${prefix}_`))?.[1].description
        : undefined,
    });

    shifted.forEach(([key, handle]) => {
      undoEntry.add(this.graph.addNodeInputHandle(this.nodeId, key, { ...handle, order: handle.order + 1 }));
    });
    return { key: newKey, undoEntry };
  }

  addPromptInputHandles() {
    this.graph.edit(() => {
      const { handles } = this.graph.store.getNodeData<ModelBaseNodeData>(this.nodeId);
      if (Array.isArray(handles.input)) return UndoRedoEntry.empty();
      const indices = Object.keys(handles.input).flatMap((key) => {
        const match = key.match(/^shot_(?:prompt|duration)_(\d+)$/);
        return match ? [Number(match[1])] : [];
      });
      const index = Math.max(1, ...indices) + 1;
      const undoEntry = UndoRedoEntry.empty();
      const order = Math.max(-1, ...Object.values(handles.input).map((handle) => handle.order ?? 0)) + 1;
      if (!handles.input.shot_duration_1) {
        const prompt = handles.input.prompt;
        const durationOrder = prompt ? prompt.order + 1 : order;
        Object.entries(handles.input).forEach(([key, handle]) => {
          if (handle.order >= durationOrder) {
            undoEntry.add(this.graph.addNodeInputHandle(this.nodeId, key, { ...handle, order: handle.order + 1 }));
          }
        });
        undoEntry.add(
          this.addInputHandleInternal({
            key: 'shot_duration_1',
            type: HandleType.Number,
            format: 'integer',
            label: 'Shot 1 duration',
            order: durationOrder,
          }),
        );
      }
      const nextOrder = order + (handles.input.shot_duration_1 ? 0 : 1);
      undoEntry.add(
        this.addInputHandleInternal({
          key: `shot_prompt_${index}`,
          type: HandleType.Text,
          format: 'text',
          label: `Shot ${index} prompt`,
          order: nextOrder,
        }),
      );
      undoEntry.add(
        this.addInputHandleInternal({
          key: `shot_duration_${index}`,
          type: HandleType.Number,
          format: 'integer',
          label: `Shot ${index} duration`,
          order: nextOrder + 1,
        }),
      );
      return undoEntry;
    });
  }

  addLoraInputHandles() {
    this.graph.edit(() => {
      const { handles } = this.graph.store.getNodeData<ModelBaseNodeData>(this.nodeId);
      const numberOfLoraInputs = Object.keys(handles.input).filter((key) => key.includes('lora')).length / 2;
      const loraKey = `lora_${numberOfLoraInputs + 1}`;
      const weightKey = `lora_${numberOfLoraInputs + 1}_scale`;
      const order = Object.keys(handles.input).length;

      const undoEntry = UndoRedoEntry.empty();
      undoEntry.add(
        this.addInputHandleInternal({
          key: loraKey,
          format: 'uri',
          label: `loRA_${numberOfLoraInputs + 1}`,
          order,
          type: HandleType.Lora,
        }),
      );
      undoEntry.add(
        this.addInputHandleInternal({
          key: weightKey,
          format: 'number',
          label: `loRA_${numberOfLoraInputs + 1}_weight`,
          order: order + 1,
          type: HandleType.Number,
        }),
      );
      return undoEntry;
    });
  }

  private getData(): ModelBaseNodeData {
    return this.graph.store.getNodeData<ModelBaseNodeData>(this.nodeId);
  }

  private getGenerations(): UploadedAsset[] {
    const data = this.graph.store.getNodeData<AiModelData>(this.nodeId);
    return data.generations?.map(ModelAssetToUploadedAsset) ?? [];
  }

  private getSelectedIndex(): number {
    const data = this.graph.store.getNodeData<AiModelData>(this.nodeId);
    return data.selectedIndex;
  }

  setViewMode(viewMode: NodeViewMode) {
    this.graph.store.updateNodeData<ModelBaseNodeData>(this.nodeId, (_) => {
      return {
        viewMode,
      };
    });
  }

  getAllDownloadableAssets(): DownloadableAsset[] {
    return this.getGenerations().filter(isMediaAsset);
  }

  /**
   * Gets a downloadable asset at a specific index
   */
  getDownloadableAssetAtIndex(index: number): DownloadableAsset | null {
    const generations = this.getGenerations();
    const asset = generations[index];

    // Check if the asset is downloadable (not text or rendering, and has a url)
    if (!asset || !isMediaAsset(asset) || !asset.url) {
      return null;
    }

    return asset;
  }

  /**
   * Gets the currently selected downloadable asset
   */
  getCurrentDownloadableAsset(): DownloadableAsset | null {
    const selectedIndex = this.getSelectedIndex();
    return this.getDownloadableAssetAtIndex(selectedIndex);
  }
}

/** True if a generation already exists for this batch run slot (avoids duplicate appends from out-of-order / replayed updates). */
function hasExistingGenerationForIncomingRunId(
  generations: UploadedAsset[],
  runIndexInBatch: number,
  batchId: string,
  secondaryOrder: number,
): boolean {
  return generations.some(
    (g) => g.batchId === batchId && g.order === runIndexInBatch && g.secondaryOrder === secondaryOrder,
  );
}

function compareSecondaryOrder(existingSecondary: number | undefined, newSecondary: number | undefined): boolean {
  // Both undefined: maintain current behavior (insert after)
  if (existingSecondary === undefined && newSecondary === undefined) {
    return true;
  }
  // Only existing is undefined: new item (with secondary order) comes first
  if (existingSecondary === undefined) {
    return false;
  }
  // Only new is undefined: insert after existing item
  if (newSecondary === undefined) {
    return true;
  }
  // Both defined: normal ascending comparison
  return existingSecondary <= newSecondary;
}

function findInsertionIndex(
  array: ModelBaseNodeData['result'],
  order: number,
  batchId: string,
  secondaryOrder?: number,
): number {
  if (!array?.length) {
    return 0;
  }

  // Find all items in the same batch (they will all have order too)
  type BatchItem = {
    item: ModelBaseNodeData['result'][number];
    index: number;
    order: number;
    secondaryOrder?: number;
  };
  const sameBatchItems: BatchItem[] = [];

  for (let i = 0; i < array.length; i++) {
    const currentItem = array[i];

    // Since batch items always have order, we can check batchId directly
    if (currentItem.batchId === batchId) {
      const order = currentItem.order as number;
      const secondaryOrder = currentItem.secondaryOrder as number | undefined;
      sameBatchItems.push({
        item: currentItem,
        index: i,
        order,
        secondaryOrder,
      });
    }
  }

  // If no items in the same batch exist, insert at the end
  if (sameBatchItems.length === 0) {
    return array.length;
  }

  // Binary search within the same batch's items
  let left = 0;
  let right = sameBatchItems.length;

  while (left < right) {
    const mid = Math.floor((left + right) / 2);
    const midItem = sameBatchItems[mid];

    const shouldInsertAfter =
      midItem.order < order ||
      (midItem.order === order && compareSecondaryOrder(midItem.secondaryOrder, secondaryOrder));

    if (shouldInsertAfter) {
      left = mid + 1;
    } else {
      right = mid;
    }
  }

  // Determine the actual array index for insertion within the batch
  if (left === 0) {
    // Insert before the first item in the batch
    return sameBatchItems[0].index;
  } else if (left === sameBatchItems.length) {
    // Insert after the last item in the batch
    return sameBatchItems[sameBatchItems.length - 1].index + 1;
  } else {
    // Insert between two items in the same batch
    return sameBatchItems[left].index;
  }
}
